import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';

import FirebaseImage from './FirebaseImage';
import { useI18n } from '../../i18n/I18nProvider';

const normalizeMedia = (images) => {
  const values = Array.isArray(images) ? images : [images];
  const seen = new Set();

  return values.reduce((result, value, index) => {
    if (!value) return result;
    const item = typeof value === 'string' ? { url: value } : value;
    const url = String(item.url || item.src || item.downloadURL || '').trim();
    if (!url || seen.has(url)) return result;
    seen.add(url);
    result.push({
      id: item.id || `${url}-${index}`,
      url,
      source: item.source || '',
      phase: item.phase || '',
    });
    return result;
  }, []);
};

const ProjectImageCarousel = ({
  images = [],
  alt,
  fallbackSrc = '/images/project-placeholder.svg',
  className,
  showMetadata = false,
}) => {
  const { t } = useI18n();
  const resolvedAlt = alt || t('carousel.defaultAlt');
  const sourceLabel = (source) => source === 'artisan' ? t('carousel.artisanPhoto') : source === 'client' ? t('carousel.clientPhoto') : '';
  const phaseLabel = (phase) => ({
    before: t('carousel.before'),
    after: t('carousel.after'),
    during: t('carousel.during'),
    detail: t('carousel.detail'),
  }[phase] || '');
  const normalizedMedia = useMemo(() => normalizeMedia(images), [images]);
  const slides = normalizedMedia.length > 0 ? normalizedMedia : [{ id: 'placeholder', url: '' }];
  const [activeIndex, setActiveIndex] = useState(0);
  const viewportRef = useRef(null);
  const dragRef = useRef({ active: false, pointerId: null, startX: 0, startScrollLeft: 0, startIndex: 0 });

  useEffect(() => {
    setActiveIndex(0);
    if (viewportRef.current) {
      viewportRef.current.scrollTo({ left: 0 });
    }
  }, [normalizedMedia.map((item) => `${item.id}:${item.url}:${item.source}:${item.phase}`).join('|')]);

  const goTo = (index) => {
    const nextIndex = (index + slides.length) % slides.length;
    setActiveIndex(nextIndex);

    const viewport = viewportRef.current;
    if (viewport) {
      viewport.scrollTo({
        left: nextIndex * viewport.clientWidth,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = () => {
    const viewport = viewportRef.current;
    if (!viewport || viewport.clientWidth === 0) return;

    const nextIndex = Math.round(viewport.scrollLeft / viewport.clientWidth);
    if (nextIndex !== activeIndex && nextIndex >= 0 && nextIndex < slides.length) {
      setActiveIndex(nextIndex);
    }
  };

  const handleKeyDown = (event) => {
    if (slides.length < 2) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(activeIndex - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(activeIndex + 1);
    }
  };

  const beginSwipe = (clientX, pointerId = null) => {
    const viewport = viewportRef.current;
    if (!viewport || slides.length < 2) return;
    dragRef.current = {
      active: true,
      pointerId,
      startX: clientX,
      startScrollLeft: viewport.scrollLeft,
      startIndex: activeIndex,
    };
  };

  const moveSwipe = (clientX, pointerId = null) => {
    const viewport = viewportRef.current;
    const drag = dragRef.current;
    if (!viewport || !drag.active) return;
    if (drag.pointerId !== null && pointerId !== null && drag.pointerId !== pointerId) return;
    viewport.scrollLeft = drag.startScrollLeft - (clientX - drag.startX);
  };

  const endSwipe = (clientX, pointerId = null) => {
    const viewport = viewportRef.current;
    const drag = dragRef.current;
    if (!viewport || !drag.active) return;
    if (drag.pointerId !== null && pointerId !== null && drag.pointerId !== pointerId) return;

    const delta = clientX - drag.startX;
    const threshold = Math.min(84, viewport.clientWidth * 0.16);
    dragRef.current.active = false;

    if (Math.abs(delta) >= threshold) {
      goTo(drag.startIndex + (delta < 0 ? 1 : -1));
    } else {
      goTo(drag.startIndex);
    }
  };

  const handlePointerDown = (event) => {
    const viewport = viewportRef.current;
    if (!viewport || slides.length < 2 || event.pointerType === 'mouse') return;
    beginSwipe(event.clientX, event.pointerId);
    viewport.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    moveSwipe(event.clientX, event.pointerId);
  };

  const finishPointerDrag = (event) => {
    const viewport = viewportRef.current;
    if (viewport) viewport.releasePointerCapture?.(event.pointerId);
    endSwipe(event.clientX, event.pointerId);
  };

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    beginSwipe(touch.clientX, 'touch');
  };

  const handleTouchMove = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    moveSwipe(touch.clientX, 'touch');
  };

  const handleTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    endSwipe(touch.clientX, 'touch');
  };

  const hasMultipleImages = normalizedMedia.length > 1;

  return (
    <Carousel
      className={className}
      role="region"
      aria-roledescription={t('carousel.roleDescription')}
      aria-label={t('carousel.regionLabel')}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <Viewport
        ref={viewportRef}
        onScroll={handleScroll}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointerDrag}
        onPointerCancel={finishPointerDrag}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Track>
          {slides.map((item, index) => (
            <Slide
              key={`${item.id || item.url || 'placeholder'}-${index}`}
              aria-hidden={index !== activeIndex}
            >
              <FirebaseImage
                src={item.url}
                fallbackSrc={fallbackSrc}
                alt={t('carousel.imageAlt', { alt: resolvedAlt, index: index + 1, total: slides.length })}
                draggable="false"
              />
              {showMetadata && (sourceLabel(item.source) || phaseLabel(item.phase)) && (
                <MediaMetadata>
                  {sourceLabel(item.source) && <SourceTag $source={item.source}>{sourceLabel(item.source)}</SourceTag>}
                  {phaseLabel(item.phase) && <PhaseTag>{phaseLabel(item.phase)}</PhaseTag>}
                </MediaMetadata>
              )}
            </Slide>
          ))}
        </Track>
      </Viewport>

      <TopOverlay>
        <PhotoLabel>
          <CollectionsOutlinedIcon />
          {normalizedMedia.length > 0
            ? t('carousel.photoCount', { count: normalizedMedia.length })
            : t('carousel.noPhoto')}
        </PhotoLabel>
        {normalizedMedia.length > 0 && (
          <Counter aria-live="polite">
            {activeIndex + 1} / {slides.length}
          </Counter>
        )}
      </TopOverlay>

      {hasMultipleImages && (
        <>
          <ArrowButton
            type="button"
            $side="left"
            aria-label={t('carousel.previous')}
            onClick={() => goTo(activeIndex - 1)}
          >
            <ArrowBackIosNewRoundedIcon />
          </ArrowButton>
          <ArrowButton
            type="button"
            $side="right"
            aria-label={t('carousel.next')}
            onClick={() => goTo(activeIndex + 1)}
          >
            <ArrowForwardIosRoundedIcon />
          </ArrowButton>

          <BottomOverlay>
            <SwipeHint>{t('carousel.swipe')}</SwipeHint>
            <Dots aria-label={t('carousel.choose')}>
              {slides.map((item, index) => (
                <Dot
                  key={`${item.id || item.url || 'dot'}-${index}`}
                  type="button"
                  $active={index === activeIndex}
                  aria-label={t('carousel.show', { index: index + 1 })}
                  aria-current={index === activeIndex ? 'true' : undefined}
                  onClick={() => goTo(index)}
                />
              ))}
            </Dots>
          </BottomOverlay>
        </>
      )}
    </Carousel>
  );
};

const Carousel = styled.div`
  position: relative;
  width: 100%;
  min-height: 18rem;
  aspect-ratio: 16 / 8.6;
  overflow: hidden;
  border-radius: 1.35rem 1.35rem 0 0;
  background: #eeeae4;
  outline: none;

  &:focus-visible {
    box-shadow: inset 0 0 0 4px rgba(192, 163, 117, 0.5);
  }

  @media (max-width: 42rem) {
    min-height: 14rem;
    aspect-ratio: 4 / 3;
  }
`;

const Viewport = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &:active { cursor: grabbing; }

  @media (hover: none) {
    cursor: default;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Track = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const Slide = styled.div`
  position: relative;
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
  background: #eeeae4;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, transparent 24%),
      linear-gradient(0deg, rgba(0, 0, 0, 0.38) 0%, transparent 30%);
  }

  img {
    width: 100%;
    height: 100%;
    display: block;
    box-sizing: border-box;
    padding: clamp(0.45rem, 1.2vw, 0.8rem);
    border-radius: 1rem;
    background: #f1ece5;
    object-fit: contain;
    object-position: center;
    user-select: none;
  }
`;

const MediaMetadata = styled.div`
  position: absolute;
  z-index: 3;
  left: 1rem;
  top: 3.65rem;
  display: flex;
  flex-wrap: wrap;
  gap: .4rem;
  pointer-events: none;

  @media (max-width: 42rem) {
    left: .65rem;
    top: 3.2rem;
  }
`;

const SourceTag = styled.span`
  padding: .34rem .52rem;
  border: 1px solid rgba(255,255,255,.42);
  border-radius: 999px;
  background: ${({ $source }) => ($source === 'artisan' ? 'rgba(192,163,117,.94)' : 'rgba(0,0,0,.82)')};
  color: #fff;
  font-size: .68rem;
  font-weight: 850;
  backdrop-filter: blur(8px);
`;

const PhaseTag = styled.span`
  padding: .34rem .52rem;
  border: 1px solid rgba(255,255,255,.5);
  border-radius: 999px;
  background: rgba(255,255,255,.92);
  color: #111;
  font-size: .68rem;
  font-weight: 900;
  backdrop-filter: blur(8px);
`;

const TopOverlay = styled.div`
  position: absolute;
  z-index: 3;
  top: 1rem;
  left: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  pointer-events: none;
`;

const PhotoLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2rem;
  padding: 0.4rem 0.65rem;
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.76);
  color: #fff;
  font-size: 0.76rem;
  font-weight: 850;
  backdrop-filter: blur(8px);

  svg {
    font-size: 1rem;
  }
`;

const Counter = styled.span`
  min-width: 3rem;
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.76);
  color: #fff;
  font-size: 0.76rem;
  font-weight: 850;
  text-align: center;
  backdrop-filter: blur(8px);
`;

const ArrowButton = styled.button`
  position: absolute;
  z-index: 4;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 1rem;' : 'right: 1rem;')}
  width: 2.75rem;
  height: 2.75rem;
  display: grid;
  place-items: center;
  transform: translateY(-50%);
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: var(--color-mainDark);
  cursor: pointer;
  box-shadow: 0 0.65rem 1.5rem rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
  transition: transform 160ms ease, background 160ms ease;

  &:hover {
    transform: translateY(-50%) scale(1.06);
    background: #fff;
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
  }

  svg {
    font-size: 1rem;
  }

  @media (max-width: 42rem) {
    width: 2.35rem;
    height: 2.35rem;
    ${({ $side }) => ($side === 'left' ? 'left: 0.65rem;' : 'right: 0.65rem;')}
  }
`;

const BottomOverlay = styled.div`
  position: absolute;
  z-index: 3;
  left: 1rem;
  right: 1rem;
  bottom: 0.9rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  pointer-events: none;

  @media (max-width: 42rem) {
    justify-content: center;
  }
`;

const SwipeHint = styled.span`
  color: #fff;
  font-size: 0.75rem;
  font-weight: 750;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);

  @media (max-width: 42rem) {
    display: none;
  }
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.4rem 0.55rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.58);
  pointer-events: auto;
  backdrop-filter: blur(8px);
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? '1.35rem' : '0.48rem')};
  height: 0.48rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.55)')};
  cursor: pointer;
  transition: width 180ms ease, background 180ms ease;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

export default ProjectImageCarousel;
